import Spinner from "@/common/Spinner";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import { Actions as FSS_REPORT_ACTIONS } from "@/redux/actions/fssReportActions";
import { ArrowDownTrayIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowUpTrayIcon, DocumentArrowUpIcon } from "@/icons/solid";
import BuddySelector from "buddy/selector";
import { useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { connect } from "react-redux";

const flatImportTypes = [
	{
		label: "EPS XML Upload",
		value: "TAX_FORM_IMPORT_EPS_XML",
		description: "Upload your EPS XML file here",
		jurisdiction: ["UK"],
		type: "EPS",
	},
	{
		label: "EPS Excel Import",
		isDisabled: true,
		isComingSoon: true,
		jurisdiction: ["UK"],
		description: "Coming Soon!",
		value: "TAX_FORM_IMPORT_EPS_EXCEL",
		type: "EPS",
	},
];

const importTypes = flatImportTypes.reduce((acc, item, index, { length }) => {
	if (item.isComingSoon) acc["Coming Soon"] = [...(acc["Coming Soon"] || []), item];
	else acc[item.type] = [...(acc[item.type] || []), item];

	if (index === length - 1) {
		return Object.entries(acc).map(([label, options]) => ({
			label,
			options,
		}));
	} else {
		return acc;
	}
}, {});

const ModuleStage = ({ importStage, uploadTemplate, setImportType, uploading, processUploadFile, uploadResults }) => {
	switch (importStage) {
		case "Choose":
			return (
				<div className="col-xs-12">
					<label>Import From</label>
					<BuddySelector
						options={importTypes}
						defaultMenuIsOpen={true}
						onChange={(item) => {
							setImportType(item.label);
						}}
					/>
				</div>
			);
		case "Import":
			return (
				<>
					<div className="relative flex py-5 items-center">
						<strong
							className="uppercase"
							style={{ color: "rgb(211, 211, 211)" }}
						>
							Import
						</strong>
						<div class="grow border-t ml-4"></div>
					</div>
					<div
						className="col-xs-12"
						style={{ marginTop: "2rem" }}
					>
						<button
							className="btn btn-success pull-right"
							onClick={processUploadFile}
							disabled={uploading}
						>
							<ArrowUpTrayIcon className="h-8 align-middle mr-2" />
							{uploading ? "Importing..." : `Import Tax Form`}
						</button>
					</div>
				</>
			);
		case "Download":
			return (
				<div
					className="flex items-center mb-5"
					style={{
						flexDirection: "column",
						justifyContent: "space-between",
					}}
				>
					<div className="mt-4">
						{uploadTemplate ? (
							<div className="btn-group">
								<a
									className="btn btn-default pull-right"
									target="_blank"
									href={uploadTemplate}
								>
									<ArrowDownTrayIcon className="mr-2 h-7 w-7 align-middle" />
									Excel Template
								</a>
							</div>
						) : (
							<div
								className="btn btn-default"
								disabled
							>
								<Spinner className="animate-spin -ml-1 mr-3 h-5 w-5 align-middle" />
								Loading Template
							</div>
						)}
					</div>
				</div>
			);
		case "Review":
			return <>Your {uploadResults?.type?.toUpperCase()} has been uploaded successfully!</>;
		default:
			return null;
	}
};

const ImportEmployeeModal = ({
	modalStatus,
	closeModal,
	uploadTemplate,
	uploadResults,
	processUploadFile,
	uploadFile,
	uploading,
	selectTaxFormImport,
	clearImportResults,
}) => {
	const [file, setFile] = useState(false);

	const importStage = useMemo(() => {
		if (uploadResults) return "Review";
		if (file) return "Import";
		if (modalStatus !== "Tax Form Import") return "Upload";
		if (modalStatus) return "Choose";
	}, [modalStatus, file, uploadResults]);

	const [message, files, fileName] = useMemo(() => {
		switch (modalStatus) {
			case "EPS XML Upload": {
				return [
					<>
						Uploaded your FPS or your employees totals to date already? If you're already got your EPS XML
						file ready, upload it below.
					</>,
					["xml"],
					"EPS XML File",
				];
			}
			default:
				return [
					<>
						Uploaded your FPS or your employees totals to date already? Just download our ready-made
						spreadsheet template, paste in the data, and upload it back when you're done.
					</>,
					["xlsx", "xls"],
					"Excel File",
				];
		}
	}, [modalStatus]);

	const selectUploadFile = (file) => {
		setFile(file);
		if (uploadResults) clearImportResults();
		uploadFile(file);
	};

	if (!modalStatus) return null;

	return (
		<div
			className="modal"
			tabindex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "scroll" }}
		>
			<div
				className="modal-dialog"
				style={{ width: importStage?.includes("Review") ? "75%" : "40%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className="block-header bg-primary-dark"
							style={{ padding: 20 }}
						>
							<ul className="block-options">
								<li>
									<button
										type="button"
										onClick={() => {
											closeModal();
											setFile(null);
										}}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">
								Import {modalStatus.replace("Import", "").replace("Upload", "").trim()}
							</h3>
						</div>
						<div className="block-content p-8">
							{!["Choose", "Review"].includes(importStage) && (
								<>
									{message}

									<div
										className="flex items-center mt-5"
										style={{
											flexDirection: "column",
											justifyContent: "space-between",
										}}
									>
										<FileUploader
											handleChange={selectUploadFile}
											name="file"
											disabled={uploading}
											children={
												<div className="upload-company-logo">
													<DocumentArrowUpIcon className="h-14" />
													{file?.name ? file?.name : `Upload or Drop ${fileName}`}
												</div>
											}
											types={files}
										/>
									</div>
								</>
							)}
							<ModuleStage
								importStage={importStage}
								uploadTemplate={uploadTemplate}
								setImportType={selectTaxFormImport}
								processUploadFile={() =>
									processUploadFile(
										flatImportTypes?.find(({ label }) => label === modalStatus)?.value,
									)
								}
								uploadResults={uploadResults}
							/>
							<div className="clearfix" />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const {
		importModal,
		uploadTemplate,
		uploading,
		uploadResults,
		filesUploading,
		filesUploaded,
		importingEmployees: importing,
	} = state.taxForms || {};

	return {
		modalStatus: importModal,
		uploadTemplate,
		uploading: uploading || importing,
		uploadResults,
		filesUploading,
		filesUploaded,
		jurisdiction: state.company?.settings?.jurisdiction,
	};
};

const mapDispatchToProps = (dispatch) => ({
	uploadFile: (file) => dispatch(EMPLOYEE_ACTIONS.uploadFile({ file })),
	clearImportResults: (file) => dispatch(FSS_REPORT_ACTIONS.clearImportResults()),
	processUploadFile: (type) => dispatch(FSS_REPORT_ACTIONS.uploadTaxForm(type)),
	closeModal: () => {
		dispatch(FSS_REPORT_ACTIONS.openTaxFormImportModal(false));
        dispatch(FSS_REPORT_ACTIONS.clearImportResults());
	},
	selectTaxFormImport: (value) => dispatch(FSS_REPORT_ACTIONS.openTaxFormImportModal(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ImportEmployeeModal);
